// Brand.tsx
// The official BlueRock brand for the AppShell sider. The icon + wordmark are
// NOT consumer-overridable — this is the canonical mark for any BlueRock
// Console. Only the trailing text label (default "Console") is configurable,
// via AppShell's `brandLabel` prop, for apps that need different wording.
//
// SVG assets live in corenet/src/assets/ and are the source of truth for the brand.

import { Typography, theme as antTheme } from 'antd';
import bluerockIcon from '../../assets/bluerock-icon.svg';
import bluerockWordmark from '../../assets/bluerock-wordmark.svg';
import { TRANSITION } from './constants';
import { isDarkBg } from './theme-utils';

const { Text } = Typography;

interface BrandProps {
  collapsed: boolean;
  /** When provided, the brand renders as a button and fires this on click —
   *  used to navigate Home. AppShell wires this from the canonical 'home'
   *  nav item. */
  onClick?: () => void;
  /** Text after the wordmark, e.g. "Console". Empty string hides it
   *  (wordmark only, no trailing gap). AppShell supplies the default. */
  label: string;
}

export function Brand({ collapsed, onClick, label }: BrandProps) {
  const { token } = antTheme.useToken();
  // The wordmark SVG is monochrome white. In a dark theme it sits on a
  // dark sider and reads correctly; in a light theme we need to invert it
  // to black so it doesn't disappear into the background.
  const wordmarkInvert = isDarkBg(token.colorBgContainer) ? undefined : 'invert(1)';
  // 22px icon at paddingLeft 25 → icon left at x=25, geometric center at
  // x=36 — same axis as the AntD menu icons in the sider below (measured:
  // collapsed nav icons center at x=36). Vertical centering needs no
  // adjustment: `alignItems: 'center'` on this row centers each child
  // within the 26px content box (48 − 14 top − 8 bottom) around its own
  // midpoint regardless of the icon's own height, landing at y=27 either
  // way (matches AppShell.tsx's floating-toggle `top: 27`).
  const layoutStyle = {
    height: 48,
    boxSizing: 'border-box' as const,
    paddingTop: 14,
    paddingBottom: 8,
    paddingRight: 16,
    paddingLeft: 25,
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    overflow: 'hidden',
    flexShrink: 0,
  };

  const content = (
    <>
      <img
        src={bluerockIcon}
        alt=""
        style={{
          width: 22,
          height: 22,
          flexShrink: 0,
        }}
      />
      {/* Wordmark + label stay in the DOM always; we animate their
          horizontal space (max-width) and opacity in lockstep with the
          sider's own width animation.
          alignItems: 'baseline' puts the label's text baseline on the
          bottom of the wordmark image (the wordmark's own baseline,
          since its SVG glyphs sit at the bottom of the viewBox) — but
          that only produces a STABLE wordmark position when a sibling
          with real font metrics participates in the baseline
          calculation. The label used to mount conditionally
          (`{label && <Text>...}`), so with no label (a consumer passing
          label="") the wordmark became the row's ONLY baseline
          participant and aligned to itself instead — a different
          reference point than with a label, so the wordmark shifted by
          a real pixel depending on whether a label happened to be set.
          (Tried `alignItems: 'center'` instead — that stabilized the
          wordmark, but broke the label's alignment to it instead, just
          moving the bug rather than fixing it.)
          Fix: mount the label unconditionally so a real text baseline
          reference always exists, and hide it via `visibility` +
          zero width (not `display: none`, which would drop it from
          baseline participation entirely) when there's no label — same
          invisible, zero-width result as before, but the wordmark now
          always aligns to the same kind of reference either way. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: label ? 5 : 0,
          overflow: 'hidden',
          flexShrink: 0,
          maxWidth: collapsed ? 0 : 200,
          opacity: collapsed ? 0 : 1,
          pointerEvents: collapsed ? 'none' : 'auto',
          transition: `max-width ${TRANSITION}, opacity ${TRANSITION}`,
        }}
      >
        <img
          src={bluerockWordmark}
          alt="BlueRock"
          style={{
            // 100% of a 16px container height, width scaled from the SVG's
            // own viewBox ratio (70.6297:12.9015 ≈ 5.4745:1) — the SVG sets
            // `preserveAspectRatio="none"`, so it stretches to exactly
            // whatever width/height it's given rather than auto-preserving
            // ratio; getting the width right here is what keeps it
            // undistorted, not the browser.
            width: 87.6,
            height: 16,
            flexShrink: 0,
            verticalAlign: 'baseline',
            filter: wordmarkInvert,
          }}
        />
        <Text
          style={{
            color: token.colorTextSecondary,
            fontSize: 15,
            lineHeight: 1,
            whiteSpace: 'nowrap',
            flexShrink: 0,
            visibility: label ? 'visible' : 'hidden',
            width: label ? undefined : 0,
            minWidth: label ? undefined : 0,
            // NOT `overflow: 'hidden'` — CSS Box Alignment synthesizes a
            // box's baseline from its own edge instead of its font's real
            // baseline whenever `overflow` is anything but `visible`. That
            // silently reintroduced the exact bug this element exists to
            // fix: with overflow:hidden here, the wordmark baseline-aligned
            // against a SYNTHESIZED reference when label="" and a REAL font
            // baseline when label was set — two different reference points
            // again, just relocated. `visibility: hidden` alone still hides
            // the glyph; letting it overflow a 0-width box paints nothing
            // (invisible) and doesn't affect sibling layout (only the box's
            // own width does, which is still 0).
          }}
        >
          {label || ' '}
        </Text>
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label="Home"
        style={{
          ...layoutStyle,
          width: '100%',
          background: 'none',
          border: 'none',
          color: 'inherit',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        {content}
      </button>
    );
  }

  return <div style={layoutStyle}>{content}</div>;
}
